/**
 * Permissions for company management.
 */
export const CompanyPermission = {
  Read: 'read:company',
  Create: 'create:company',
  Update: 'update:company',
  Delete: 'delete:company',
} as const;

/**
 * Permissions for team management.
 */
export const TeamPermission = {
  Read: 'read:team',
  Create: 'create:team',
  Update: 'update:team',
  Delete: 'delete:team',
} as const;

/**
 * Permissions for user management.
 */
export const UserPermission = {
  Read: 'read:user',
  Create: 'create:user',
  Update: 'update:user',
  Delete: 'delete:user',
  Manage: 'manage:user',
  ChangePermission: 'change-permission:user',
} as const;

/**
 * Permissions for API user management.
 */
export const ApiUserPermission = {
  Read: 'read:api-user',
  Create: 'create:api-user',
  Update: 'update:api-user',
  Delete: 'delete:api-user',
} as const;

/**
 * Permissions for phone management.
 */
export const PhonePermission = {
  Read: 'read:phone',
  Create: 'create:phone',
  Update: 'update:phone',
  Delete: 'delete:phone',
} as const;

/**
 * Permissions for invitation management.
 */
export const InvitationPermission = {
  Read: 'read:invitation',
  Create: 'create:invitation',
  Update: 'update:invitation',
  Delete: 'delete:invitation',
  Manage: 'manage:invitation',
} as const;

/**
 * Permissions for contact management.
 */
export const ContactPermission = {
  Read: 'read:contact',
  Create: 'create:contact',
  Update: 'update:contact',
  Delete: 'delete:contact',
} as const;

/**
 * Permissions for contact-company relationship management.
 */
export const ContactCompanyPermission = {
  Read: 'read:contact-company',
  Create: 'create:contact-company',
  Update: 'update:contact-company',
  Delete: 'delete:contact-company',
} as const;

/**
 * Permissions for call management.
 */
export const CallPermission = {
  Read: 'read:call',
  Create: 'create:call',
  Update: 'update:call',
  Delete: 'delete:call',
} as const;

/**
 * Permissions for recording management.
 */
export const RecordingPermission = {
  Read: 'read:recording',
  Create: 'create:recording',
  Update: 'update:recording',
  Delete: 'delete:recording',
} as const;

/**
 * Permissions for transcription management.
 */
export const TranscriptionPermission = {
  Read: 'read:transcription',
  Create: 'create:transcription',
  Update: 'update:transcription',
  Delete: 'delete:transcription',
} as const;

/**
 * Permissions for call analysis management.
 */
export const CallAnalysisPermission = {
  Read: 'read:call-analysis',
  Create: 'create:call-analysis',
  Update: 'update:call-analysis',
  Delete: 'delete:call-analysis',
} as const;

/**
 * Permissions for boosted-word management.
 */
export const BoostedWordPermission = {
  Read: 'read:boosted-word',
  Create: 'create:boosted-word',
  Update: 'update:boosted-word',
  Delete: 'delete:boosted-word',
} as const;

/**
 * Permissions for opportunity management.
 */
export const OpportunityPermission = {
  Read: 'read:opportunity',
  Create: 'create:opportunity',
  Update: 'update:opportunity',
  Delete: 'delete:opportunity',
  Manage: 'manage:opportunity',
} as const;

/**
 * Permissions for funnel management.
 */
export const FunnelPermission = {
  Read: 'read:funnel',
  Create: 'create:funnel',
  Update: 'update:funnel',
  Delete: 'delete:funnel',
} as const;

/**
 * Permissions for funnel-status management.
 */
export const FunnelStatusPermission = {
  Read: 'read:funnel-status',
  Create: 'create:funnel-status',
  Update: 'update:funnel-status',
  Delete: 'delete:funnel-status',
  ManageSystem: 'manage:funnel-status',
} as const;

/**
 * Permissions for setting management.
 */
export const SettingPermission = {
  /** Allows changing policies restricted to tenant administrators. */
  ManageTenant: 'manage:tenant-settings',
  Read: 'read:setting',
  Create: 'create:setting',
  Update: 'update:setting',
  Delete: 'delete:setting',
} as const;

/**
 * Permissions for integration management.
 */
export const IntegrationPermission = {
  Read: 'read:integration',
  Create: 'create:integration',
  Update: 'update:integration',
  Delete: 'delete:integration',
  SyncNow: 'sync-now:integration',
  ManageWhatsAppBilling: 'manage:whatsapp-billing',
} as const;

/**
 * Permissions for integration-address management.
 */
export const IntegrationAddressPermission = {
  Read: 'read:integration-address',
  Create: 'create:integration-address',
  Update: 'update:integration-address',
  Delete: 'delete:integration-address',
} as const;

/**
 * Permissions for conversation management.
 */
export const ConversationPermission = {
  Read: 'read:conversation',
  Create: 'create:conversation',
  Update: 'update:conversation',
  Delete: 'delete:conversation',
} as const;

/**
 * Permissions for message management.
 */
export const MessagePermission = {
  Read: 'read:message',
  Create: 'create:message',
  Update: 'update:message',
  Delete: 'delete:message',
} as const;

/**
 * Permissions for legacy reports and the widget dashboard.
 */
export const ReportPermission = {
  Read: 'read:report',
  DashboardRead: 'read:report-dashboard',
  DashboardUpdate: 'update:report-dashboard',
  DashboardDelete: 'delete:report-dashboard',
  Create: 'create:report',
  Update: 'update:report',
  Delete: 'delete:report',
  AssignmentRankingRead: 'read:report-assignment-ranking',
} as const;

/**
 * Permissions for product management.
 */
export const ProductPermission = {
  Read: 'read:product',
  Create: 'create:product',
  Update: 'update:product',
  Delete: 'delete:product',
} as const;

/**
 * Permissions for managing credentials.
 */
export const CredentialPermission = {
  Read: 'read:credential',
  Create: 'create:credential',
  Update: 'update:credential',
  Delete: 'delete:credential',
  RevealSecret: 'reveal:credential-secret',
  Manage: 'manage:credential',
} as const;

/**
 * Permissions for tenant website management.
 */
export const WebsitePermission = {
  Read: 'read:website',
  Update: 'update:website',
} as const;

/**
 * Permissions for website template registry management.
 */
export const WebsiteTemplateRegistryPermission = {
  Read: 'read:website-template-registry',
  Create: 'create:website-template-registry',
  Update: 'update:website-template-registry',
  Publish: 'publish:website-template-registry',
} as const;

/**
 * Permissions for managing automation employees.
 */
export const AutomationEmployeePermission = {
  Read: 'read:automation-employee',
  Create: 'create:automation-employee',
  Update: 'update:automation-employee',
  Delete: 'delete:automation-employee',
} as const;

/**
 * Permissions for managing automation employee executions.
 */
export const AutomationEmployeeExecutionPermission = {
  Read: 'read:automation-employee-execution',
  Create: 'create:automation-employee-execution',
  Update: 'update:automation-employee-execution',
  Delete: 'delete:automation-employee-execution',
} as const;

/**
 * Permissions for managing automation employee assistances.
 */
export const AutomationEmployeeAssistancePermission = {
  Read: 'read:automation-employee-assistance',
  Resolve: 'resolve:automation-employee-assistance',
  ManageInternal: 'manage:automation-employee-internal-assistance',
} as const;

/**
 * Permissions for managing automation employee workspaces.
 */
export const AutomationEmployeeWorkspacePermission = {
  Read: 'read:automation-employee-workspace',
  Create: 'create:automation-employee-workspace',
  Update: 'update:automation-employee-workspace',
  Delete: 'delete:automation-employee-workspace',
} as const;

/**
 * Permissions for managing automation workflows.
 */
export const AutomationWorkflowPermission = {
  Read: 'read:automation-workflow',
  Create: 'create:automation-workflow',
  Update: 'update:automation-workflow',
  Delete: 'delete:automation-workflow',
} as const;

/**
 * Canonical permissions for managing automation workflow triggers.
 */
export const AutomationWorkflowTriggerPermission = {
  Read: 'read:automation-workflow-trigger',
  Create: 'create:automation-workflow-trigger',
  Update: 'update:automation-workflow-trigger',
  Delete: 'delete:automation-workflow-trigger',
} as const;

/**
 * Permissions for managing automation workflow executions.
 */
export const AutomationWorkflowExecutionPermission = {
  Read: 'read:automation-workflow-execution',
  Create: 'create:automation-workflow-execution',
  Update: 'update:automation-workflow-execution',
  Delete: 'delete:automation-workflow-execution',
} as const;

/**
 * Canonical permissions for reading and managing workflow templates.
 */
export const TemplateAppPermission = {
  Read: 'read:automation-workflow-template',
  Create: 'create:automation-workflow-template',
  Update: 'update:automation-workflow-template',
  Delete: 'delete:automation-workflow-template',
} as const;

/**
 * Permissions for managing automation actions.
 */
export const AutomationActionPermission = {
  Read: 'read:automation-action',
  Create: 'create:automation-action',
  Update: 'update:automation-action',
  Delete: 'delete:automation-action',
} as const;

/**
 * Permissions for managing custom fields.
 */
export const CrmCustomFieldPermission = {
  Create: 'create:crm-custom-field',
  Update: 'update:crm-custom-field',
  Delete: 'delete:crm-custom-field',
} as const;

/**
 * Permissions for tenant-assume admin flows.
 */
export const AdminTenantPermission = {
  Assume: 'assume:tenant',
} as const;

/**
 * Permissions for admin-managed TEST conversation flows.
 */
export const TestConversationPermission = {
  Manage: 'manage:test-conversation',
} as const;

/** Independent property actions and assignment scopes. */
export const PropertyPermission = {
  Read: 'read:property',
  ReadScoped: 'read:property:scoped',
  Update: 'update:property',
  UpdateScoped: 'update:property:scoped',
  Create: 'create:property',
  Publish: 'publish:property',
  PublishScoped: 'publish:property:scoped',
} as const;

/** Permissions for accessing file content. */
export const FilePermission = {
  Read: 'read:file',
} as const;
